// A1 Patch D — IndexedDB-backed per-season fetch cache. Yahoo's 1.1s
// throttle dominates fetch time for multi-season leagues (Smithtown ~8min).
// Past-season data is immutable (final standings + matchups + trades all
// closed), so caching it makes returning visits near-instant.
//
// Scope:
//   - Per-season-per-data-type entries: { league_id, year, data_type } → blob
//   - Schema version suffix on the key (v1) protects against future shape
//     changes — bump the const, old entries become misses, refetched.
//   - Current year is ALWAYS bypassed (in-season data still evolves).
//     Past years cache-hit on returning loads.
//
// Budget + eviction:
//   - 5MB per league_id (rough estimate per league: Smithtown ~1MB,
//     dynasty leagues with 6yrs ~3MB. 5MB headroom covers extremes.)
//   - LRU eviction: each get() bumps lastAccessedAt; sets that bring the
//     league prefix over budget evict oldest by lastAccessedAt until
//     under budget.
//
// Failure modes:
//   - IndexedDB unavailable (Safari private, blocked) → all gets return
//     null, all sets noop. Callers fall through to the network path.
//     Cache layer is best-effort — never blocks the recap.

const DB_NAME = "almanacCache";
const DB_VERSION = 1;
const STORE = "seasons";
// BUMP THIS whenever the cached payload shape OR any downstream computation
// that reads it changes - prevents stale-cache-masks-fix bugs. The version
// is suffixed onto every cache key, so bumping invalidates all existing
// entries and forces a fresh fetch on next load. Do NOT auto-bump per
// deploy (that nukes the cache benefit and forces every user through the
// ~8-minute Yahoo refetch every time); it's a manual bump tied to a
// specific shape/computation change with a one-line note of why.
//
// History:
//   v1 - initial schema.
//   v2 - Yahoo lineup URI + parse fix (3e029a5 / 6c723d8), Yahoo
//        league_settings expansion (abf287d), Yahoo league_settings
//        roster_positions / stat_modifiers raw passthrough (e1ec3b8),
//        SCORING_DETAIL_v1 normalized scoring + non-core fingerprint
//        (735eedb). Stale v1 entries were masking all four shape
//        upgrades in prod.
const SCHEMA_VERSION = "v2";
const PER_LEAGUE_BUDGET_BYTES = 5 * 1024 * 1024; // 5MB

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  if (typeof indexedDB === "undefined") {
    _dbPromise = Promise.resolve(null);
    return _dbPromise;
  }
  _dbPromise = new Promise((resolve) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "key" });
        // Index for budget queries by league prefix. Keys are
        // `${leagueId}__${year}__${dataType}__${SCHEMA_VERSION}` so a
        // prefix scan on leagueId aggregates all entries for that league.
        store.createIndex("leagueId", "leagueId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null); // best-effort
    req.onblocked = () => resolve(null);
  });
  return _dbPromise;
}

function buildKey(leagueId, year, dataType) {
  return `${leagueId}__${year}__${dataType}__${SCHEMA_VERSION}`;
}

function estimateBytes(data) {
  try {
    // Rough but fast. Real IndexedDB entries are structured-cloned which
    // is more compact, but JSON length is a useful upper-bound estimate
    // for budget enforcement.
    return JSON.stringify(data).length * 2; // UTF-16 chars
  } catch {
    return 0;
  }
}

// Returns cached data or null. Updates lastAccessedAt on hit (write-back
// not awaited — fire-and-forget). Current-year detection is the caller's
// responsibility — caller should skip get() entirely for current year.
export async function getCached(leagueId, year, dataType) {
  const db = await openDb();
  if (!db) return null;
  const key = buildKey(leagueId, year, dataType);
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const entry = req.result;
        if (!entry) { resolve(null); return; }
        // Touch lastAccessedAt for LRU. Separate write tx so we don't
        // block the get path.
        touchLru(leagueId, key);
        resolve(entry.data);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function touchLru(leagueId, key) {
  openDb().then((db) => {
    if (!db) return;
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const getReq = store.get(key);
      getReq.onsuccess = () => {
        const entry = getReq.result;
        if (!entry) return;
        entry.lastAccessedAt = Date.now();
        store.put(entry);
      };
    } catch { /* best-effort */ }
  });
}

// Caches data + enforces budget. If this set would push the league's
// total over budget, evict oldest LRU entries until under. Best-effort —
// any error returns silently (caller already has the data, cache is a
// nice-to-have).
export async function setCached(leagueId, year, dataType, data) {
  const db = await openDb();
  if (!db) return;
  const key = buildKey(leagueId, year, dataType);
  const sizeBytes = estimateBytes(data);
  const now = Date.now();
  const entry = {
    key,
    leagueId,
    year: String(year),
    dataType,
    data,
    sizeBytes,
    savedAt: now,
    lastAccessedAt: now,
  };
  try {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(entry);
    await new Promise((resolve) => { tx.oncomplete = resolve; tx.onerror = resolve; });
    // Budget check after the write — if over, evict.
    await enforceBudget(leagueId);
  } catch { /* best-effort */ }
}

// LRU eviction within a single league. Scan all entries for this league
// via the leagueId index, sum sizeBytes, evict the oldest lastAccessedAt
// until under budget.
async function enforceBudget(leagueId) {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const index = store.index("leagueId");
      const req = index.getAll(leagueId);
      req.onsuccess = () => {
        const entries = req.result || [];
        let total = entries.reduce((s, e) => s + (e.sizeBytes || 0), 0);
        if (total <= PER_LEAGUE_BUDGET_BYTES) { resolve(); return; }
        entries.sort((a, b) => (a.lastAccessedAt || 0) - (b.lastAccessedAt || 0));
        for (const e of entries) {
          if (total <= PER_LEAGUE_BUDGET_BYTES) break;
          store.delete(e.key);
          total -= (e.sizeBytes || 0);
        }
        resolve();
      };
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

// Debug / reset helper. Not currently wired into UI but useful for
// testing + future "Force-refresh this league" affordance.
export async function clearLeague(leagueId) {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const index = store.index("leagueId");
      const req = index.getAllKeys(leagueId);
      req.onsuccess = () => {
        for (const key of req.result || []) store.delete(key);
        resolve();
      };
      req.onerror = () => resolve();
    } catch { resolve(); }
  });
}

// Convenience wrapper — try cache, fall through to fetcher on miss,
// write-back on success. Returns { data, fromCache }. Caller decides
// whether to skip get() for current year by passing isCurrentYear: true.
export async function withCache({ leagueId, year, dataType, isCurrentYear, fetcher }) {
  if (!isCurrentYear) {
    const cached = await getCached(leagueId, year, dataType);
    if (cached !== null && cached !== undefined) {
      return { data: cached, fromCache: true };
    }
  }
  const data = await fetcher();
  // Only cache successful fetches with real data. Don't poison cache
  // with empty arrays/objects that suggest a fetch failure.
  if (data !== null && data !== undefined && !isCurrentYear) {
    setCached(leagueId, year, dataType, data); // fire-and-forget
  }
  return { data, fromCache: false };
}
